export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: 'ADMIN' | 'MANAGER' | 'VIEWER';
  active: boolean;
  lastLogin: string;
  createdAt: string;
}

// Reference Data Types (managed in Settings)
export interface RefSupplier {
  id: string;
  name: string;
  websiteUrl?: string;
  sortOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RefColor {
  id: string;
  name: string;
  hexCode: string;
  sortOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RefBrand {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RefSupplyLookup {
  id: string;
  category: string;
  value: string;
  label: string;
  parentValue?: string | null;
  metadataJson?: string | null;
  sortOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ProductType =
  | 'STANDARD'
  | 'THREE_D_PRINTED'
  | 'PRINT_ON_DEMAND'
  | 'VINYL_CUTTING'
  | 'LASER_ENGRAVED'
  | 'UV_PRINTED'
  | 'APPAREL'
  | 'SUBLIMATION'
  | 'BUTTON'
  | 'EMBROIDERY';

export type SupplyClass =
  | 'CUSTOMIZABLE_BLANK'
  | 'CONSUMABLE'
  | 'HARDWARE_COMPONENT'
  | 'PACKAGING'
  | 'BLANK_APPAREL'
  | 'ELECTRONIC'
  | 'RAW_MATERIAL'
  | 'MAINTENANCE_PART'
  | 'ACCESSORY'
  | 'OTHER';

export type SupplyType =
  // 🧱 Blanks (broad + specific)
  | 'BLANK_SUBSTRATE'
  | 'BLANK_APPAREL'
  | 'DOG_TAG_BLANK'
  | 'ACRYLIC_BLANK'
  | 'ORNAMENT_BLANK'
  | 'KEYCHAIN_BLANK'
  | 'TUMBLER_BLANK'
  | 'MAGNET_BLANK'
  | 'TROPHY_BLANK'
  | 'TOKEN_BLANK'
  | 'SIGN_BLANK'
  | 'BUTTON_BLANK'
  | 'PATCH_BLANK'
  | 'COASTER_BLANK'
  | 'STICKER_BLANK'
  | 'DECAL_BLANK'
  | 'OTHER_BLANK'
  // 👕 Blank Apparel (broad + specific)
  | 'SHIRT_BLANK'
  | 'HOODIE_BLANK'
  | 'HAT_BLANK'
  | 'T_SHIRT'
  | 'TANK_TOP'
  | 'HOODIE'
  | 'SWEATSHIRT'
  | 'POLO'
  | 'HAT'
  | 'BEANIE'
  | 'APRON'
  | 'TOTE_BAG'
  | 'APPAREL_PATCH'
  | 'OTHER_APPAREL'
  // 🧪 Consumables
  | 'FILAMENT'
  | 'UV_INK'
  | 'SUBLIMATION_INK'
  | 'THREAD'
  | 'VINYL'
  | 'HEAT_TRANSFER'
  | 'TRANSFER_TAPE'
  | 'LASER_MATERIAL'
  | 'SUBLIMATION_PAPER'
  | 'ADHESIVE'
  | 'RESIN'
  | 'CLEANING_FLUID'
  | 'LUBRICANT'
  | 'SOLVENT'
  | 'PRINTER_INK'
  | 'TONER'
  | 'OTHER_CONSUMABLE'
  // 🔩 Hardware (broad + specific)
  | 'HARDWARE'
  | 'FASTENER'
  | 'KEY_RING'
  | 'JUMP_RING'
  | 'MAGNET'
  | 'CHAIN'
  | 'SCREW'
  | 'BOLT'
  | 'NUT'
  | 'WASHER'
  | 'LED'
  | 'PIN_BACK'
  | 'CLASP'
  | 'HINGE'
  | 'HOOK'
  | 'FASTENER'
  | 'OTHER_HARDWARE'
  // 📦 Packaging (broad + specific)
  | 'PACKAGING'
  | 'POLY_BAG'
  | 'ZIP_BAG'
  | 'BUBBLE_MAILER'
  | 'SHIPPING_BOX'
  | 'GIFT_BOX'
  | 'PRODUCT_INSERT'
  | 'THANK_YOU_CARD'
  | 'LABEL_SUPPLY'
  | 'PACKING_SLIP'
  | 'TISSUE_PAPER'
  | 'SHRINK_WRAP'
  | 'OTHER_PACKAGING'
  // ⚡ Electronic (broad + specific)
  | 'ELECTRONIC'
  | 'LED_MODULE'
  | 'LED_STRIP'
  | 'BATTERY'
  | 'POWER_SUPPLY'
  | 'USB_CABLE'
  | 'SWITCH'
  | 'CONTROLLER'
  | 'CHARGER'
  | 'DISPLAY'
  | 'SENSOR'
  | 'OTHER_ELECTRONIC'
  // 🧵 Raw Material (broad + specific)
  | 'RAW_MATERIAL'
  | 'ACRYLIC_SHEET'
  | 'MDF_SHEET'
  | 'PLYWOOD_SHEET'
  | 'LEATHER_SHEET'
  | 'STAINLESS_STEEL_SHEET'
  | 'ALUMINUM_SHEET'
  | 'FABRIC_MATERIAL'
  | 'CORK_MATERIAL'
  | 'RUBBER_MATERIAL'
  | 'FOAM_MATERIAL'
  | 'OTHER_RAW_MATERIAL'
  // 🛠️ Maintenance (broad + specific)
  | 'MAINTENANCE_PART'
  | 'NOZZLE'
  | 'BUILD_PLATE'
  | 'PTFE_TUBE'
  | 'EXTRUDER_GEAR'
  | 'BELT'
  | 'BEARING'
  | 'CLEANING_BRUSH'
  | 'MAINTENANCE_KIT'
  | 'REPLACEMENT_BLADE'
  | 'FILTER'
  | 'OTHER_MAINTENANCE'
  // 📎 Accessory (broad + specific)
  | 'ACCESSORY'
  | 'CHARM'
  | 'PENDANT'
  | 'ORNAMENT_ATTACHMENT'
  | 'DECORATIVE_BEAD'
  | 'RIBBON'
  | 'LANYARD'
  | 'CLIP_ACCESSORY'
  | 'STAND'
  | 'EASEL'
  | 'DISPLAY_HOLDER'
  | 'OTHER_ACCESSORY'
  // Other
  | 'OTHER';

export type StorageLocationType = 'ROOM' | 'SHELF' | 'BIN' | 'CONTAINER' | 'DRAWER' | 'CABINET';

export interface StorageLocation {
  id: string;
  name: string;
  description: string;
  locationType: StorageLocationType;
  locationTypeDisplay: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  children: StorageLocation[];
}

export const PRODUCT_TYPE_META: Record<ProductType, { label: string; icon: string; craftCategory: string; usesDesignFile: boolean; uses3dModel: boolean }> = {
  STANDARD:       { label: 'Standard Product',    icon: '📦', craftCategory: 'general',      usesDesignFile: false, uses3dModel: false },
  PRINT_ON_DEMAND:{ label: 'Print on Demand',      icon: '🖨️', craftCategory: 'general',      usesDesignFile: false, uses3dModel: false },
  THREE_D_PRINTED:{ label: '3D Printed',           icon: '🖨️', craftCategory: '3d_printing',  usesDesignFile: false, uses3dModel: true  },
  VINYL_CUTTING:  { label: 'Vinyl / Cricut',        icon: '✂️', craftCategory: 'vinyl',        usesDesignFile: true,  uses3dModel: false },
  LASER_ENGRAVED: { label: 'Laser Engraved',        icon: '🔆', craftCategory: 'laser',        usesDesignFile: true,  uses3dModel: false },
  UV_PRINTED:     { label: 'UV Printed',            icon: '💡', craftCategory: 'uv_printing',  usesDesignFile: true,  uses3dModel: false },
  APPAREL:        { label: 'Apparel / T-Shirt',     icon: '👕', craftCategory: 'apparel',      usesDesignFile: true,  uses3dModel: false },
  SUBLIMATION:    { label: 'Sublimation',           icon: '🌈', craftCategory: 'sublimation',  usesDesignFile: true,  uses3dModel: false },
  BUTTON:         { label: 'Button / Pin',          icon: '🔘', craftCategory: 'buttons',      usesDesignFile: true,  uses3dModel: false },
  EMBROIDERY:     { label: 'Embroidery',            icon: '🪡', craftCategory: 'embroidery',   usesDesignFile: true,  uses3dModel: false },
};

export interface Supply {
  id: string;
  sku: string;
  manufacturerSku?: string;
  manufacturerSku2?: string;
  manufacturerSku3?: string;
  name: string;
  description: string;
  supplyType: SupplyType;
  supplyTypeDisplay?: string;
  supplyClass?: SupplyClass;
  supplyClassDisplay?: string;
  quantityInStock: number;
  unitOfMeasure: string;
  costPerUnit: number;
  stockValue?: number;
  supplier: string;
  supplierSku?: string;
  purchaseUrl?: string;
  category: string;
  reorderLevel: number;
  reorderQuantity: number;
  location: string;
  storageLocationId: string | null;
  storageNotes: string | null;
  notes: string;
  imageUrl: string | null;
  dimensionHeight?: number | null;
  dimensionWidth?: number | null;
  dimensionDepth?: number | null;
  dimensionUnit?: string | null;
  isBlank?: boolean;
  blankNotes?: string | null;
  finish?: string | null;
  coating?: string | null;
  colorSummary?: string | null;
  weight?: number | null;
  weightUnit?: string | null;
  blankShape?: string | null;
  printableAreaWidth?: number | null;
  printableAreaHeight?: number | null;
  engravableAreaWidth?: number | null;
  engravableAreaHeight?: number | null;
  isDoubleSided?: boolean;
  isCurvedSurface?: boolean;
  apparelSize?: string | null;
  filamentMaker?: string | null;
  filamentType?: string | null;
  filamentDiameter?: string | null;
  filamentNetWeight?: string | null;
  hardwareType?: string | null;
  hardwareOuterDiameter?: number | null;
  hardwareInnerDiameter?: number | null;
  hardwareWireThickness?: number | null;
  hardwareOpeningStyle?: string | null;
  productionMethodIds?: string[];
  compatibleItemTypeIds?: string[];
  compatibleMachineIds?: string[];
  isActive: boolean;
  isLowStock?: boolean;
  supplierId?: string | null;
  colorId?: string | null;
  brandId?: string | null;
  materialType?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Machine {
  id: string;
  code: string;
  name: string;
  description?: string;
  productionMethodId?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface SupplySummary {
  id: string;
  sku: string;
  name: string;
  supplyType: SupplyType;
  quantityInStock: number;
  unitOfMeasure: string;
  costPerUnit: number;
  imageUrl: string | null;
  isActive: boolean;
}

export interface ProductTypeInfo {
  type: ProductType;
  displayName: string;
  description: string;
  tracksInventory: boolean;
  icon: string;
}

export interface Filament {
  id: string;
  brand: string;
  material: string;
  colorName: string;
  colorHex: string;
  sku: string;
  stockRemainingGrams: number;
  costPerKg: number;
  displayName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  phone: string;
  managerName: string;
  commissionRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ShelfInventory {
  id: string;
  productId?: string;
  productVariantId: string;
  productName: string;
  variantSku: string;
  locationId: string;
  locationName: string;
  quantityOnShelf: number;
  quantityReserved: number;
  availableQuantity: number;
  lastCountedAt: string;
  notes: string;
  // Bin fields (V23)
  binLocation: string | null;
  binNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VariantFilament {
  id: string;
  variantId: string;
  filament: SupplySummary;  // Uses supply (filament type)
  quantityGrams: number;
  sortOrder: number;
  notes: string | null;
  costContribution: number;
  sufficientStock: boolean;
  unitsMakeable: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductVariant {
  id: string;
  productId: string;
  variantSku: string;
  fullSku: string;
  name: string | null;
  displayName: string;
  // Legacy single filament support
  filament: Filament | null;
  // NEW: Multi-filament BOM
  variantFilaments: VariantFilament[];
  totalGrams: number;
  totalFilamentCost: number;
  unitsMakeable: number;  // Based on minimum filament + supply stock
  hasSufficientStock: boolean;
  modelFilePath: string | null;
  modelExternalUrl: string | null;
  // Per-variant design file (V20) — used for UV print, laser, sublimation variants
  designExternalUrl: string | null;
  designNotes: string | null;
  imageUrl: string | null;
  notes: string;
  isActive: boolean;
  shelfInventories: ShelfInventory[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductFilament {
  id: string;
  productId: string;
  filamentId: string;
  filament: Filament;
  displayOrder: number;
  createdAt: string;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;              // Legacy single-category (kept for backward compat)
  categories?: Category[];       // Multi-category (V20)
  basePrice: number;
  cost: number;
  // Consolidated dimensions (H x W x D)
  dimensionHeight: number | null;
  dimensionWidth: number | null;
  dimensionDepth: number | null;
  // Legacy fields (deprecated)
  height?: number | null;
  width?: number | null;
  length?: number | null;
  sizeHeight?: number | null;
  sizeWidth?: number | null;
  sizeDepth?: number | null;
  productType: ProductType;
  craftType: string | null;
  tracksInventory: boolean;
  is3dPrinted: boolean;
  wordpressProductId: number | null;
  isActive: boolean;
  variants: ProductVariant[];
  productFilaments?: ProductFilament[];
  // Image support
  primaryImageUrl: string | null;
  imageUrls: string[];
  // Design / model file support
  modelFilePath: string | null;
  modelExternalUrl: string | null;
  designFilePath: string | null;
  designFileType: string | null;
  designFileName: string | null;
  designExternalUrl: string | null;
  craftMetadata: string | null;
  // Material and UPC fields (V22)
  material: string | null;
  upcCode: string | null;
  upcCode2: string | null;
  systemUpc: string | null;
  productDesign?: string | null;
  itemTypeId?: string | null;
  collectionId?: string | null;
  productionMethodIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Sale {
  id: string;
  productId?: string;
  productVariantId: string;
  productVariantSku: string;
  productName: string;
  locationId: string;
  locationName: string;
  quantity: number;
  salePrice: number;
  totalSaleAmount: number;
  saleDate: string;
  isPaidToLocation: boolean;
  isPaymentReceived: boolean;
  locationCommission: number;
  retainedEarnings: number;
  totalCommission: number;
  totalRetained: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface Transfer {
  id: string;
  productId?: string;
  productVariantId: string;
  productVariantSku: string;
  productName: string;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  quantity: number;
  transferDate: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  tokenType: string;
  expiresIn: number;
  user: User;
}

export interface ProductSupplyRequest {
  supplyId: string;
  quantityRequired: number;
  unitOfMeasure?: string;
  notes?: string;
}

export interface CreateProductRequest {
  sku?: string;
  name: string;
  description: string;
  category: string;
  basePrice: number;
  cost: number;
  // Consolidated dimensions (H x W x D)
  dimensionHeight?: number | null;
  dimensionWidth?: number | null;
  dimensionDepth?: number | null;
  productType: ProductType;
  craftType?: string | null;
  tracksInventory?: boolean;
  is3dPrinted: boolean;
  wordpressProductId: number | null;
  variants?: CreateVariantRequest[];
  filamentIds?: string[];
  // Bill of Materials — supplies used to make this product
  supplies?: ProductSupplyRequest[];
  // Image support
  primaryImageUrl?: string;
  imageUrls?: string[];
  // Design / model file support
  designExternalUrl?: string | null;
  designFilePath?: string | null;
  designFileType?: string | null;
  designFileName?: string | null;
  modelFilePath?: string | null;
  modelExternalUrl?: string | null;
  craftMetadata?: string | null;
  // Multi-category (V20)
  categoryIds?: string[];
  // Blank supply to auto-fill dimensions and add to BOM
  blankSupplyId?: string | null;
  // Material and UPC fields (V22)
  material?: string | null;
  upcCode?: string | null;
  upcCode2?: string | null;
  systemUpc?: string | null;
  productDesign?: string | null;
  itemTypeId?: string | null;
  collectionId?: string | null;
  productionMethodIds?: string[];
}

export interface VariantFilamentRequest {
  supplyId: string;
  grams: number;
  sortOrder?: number;
  notes?: string;
  extruderSlot?: number | null;
}

export interface CreateVariantRequest {
  variantSku?: string;
  name: string;                   // Required: the style/version display name
  // Legacy single filament support
  filamentId?: string | null;
  // NEW: Multi-filament BOM configuration
  variantFilaments?: VariantFilamentRequest[];
  modelExternalUrl?: string | null;
  // Per-variant design file (V20) — for UV print, laser, sublimation variants
  designExternalUrl?: string | null;
  designNotes?: string | null;
  imageUrl?: string;
  notes?: string;
  initialQuantity?: number;
  locationId?: string | null;
  // Bin fields for initial inventory placement (V23)
  binLocation?: string | null;
  binNumber?: string | null;
  variantSupplies?: ProductSupplyRequest[];
}

export interface FilamentDeduction {
  filamentId: string;
  filamentName: string;
  gramsNeeded: number;
  gramsAvailable: number;
  sufficient: boolean;
}

export interface ProductionResult {
  variantId: string;
  variantSku: string;
  quantityProduced: number;
  locationId: string;
  locationName: string;
  filamentDeductions: FilamentDeduction[];
  totalCost: number;
  producedAt: string;
  previewOnly?: boolean;
}

export interface ProduceVariantRequest {
  variantId: string;
  quantity: number;
  locationId: string;
  notes?: string;
}

export interface AuditLogEntry {
  id: string;
  actionType: string;
  entityType: string;
  entityId: string | null;
  description: string;
  oldValues: string | null;
  newValues: string | null;
  userId: string | null;
  userName: string | null;
  ipAddress: string | null;
  locationId: string | null;
  locationName: string | null;
  createdAt: string;
}

export interface UploadedFile {
  filename: string;
  originalFilename: string;
  contentType: string;
  size: number;
  url: string;
}

export interface DesignFile {
  id: string;
  entityType: string;
  entityId: string;
  fileName: string;
  originalName: string;
  filePath: string;
  urlPath: string;
  fileType: string;
  contentType: string;
  fileSize: number;
  description: string | null;
  sortOrder: number;
  isPrimary: boolean;
  uploadedBy: string | null;
  createdAt: string;
}

export interface DashboardStats {
  totalProducts: number;
  total3dPrintedProducts: number;
  totalFilaments: number;
  lowStockCount: number;
  totalLocations: number;
  inventoryByLocation: {
    locationName: string;
    totalItems: number;
    retailValue: number;
    costValue: number;
  }[];
  totalSales: number;
  totalRevenue: number;
  totalCommissionsPaid: number;
  totalRetainedEarnings: number;
  unpaidCommissions: number;
  recentSales: {
    productName: string;
    variantSku: string;
    locationName: string;
    quantity: number;
    salePrice: number;
    saleDate: string;
  }[];
  lowStockItems: {
    productName: string;
    variantSku: string;
    locationName: string;
    quantity: number;
  }[];
}

export interface ItemType {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductCollection {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductionMethod {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  supplyTypes: SupplyType[];
  usesDesignFile: boolean;
  uses3dModel: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserPreferences {
  productFormMode: 'wizard' | 'single';
  supplyFormMode: 'wizard' | 'single';
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark';
}

export interface ProductDraft {
  id: string;
  userId: string;
  payload: ProductDraftPayload;
  currentStep: string | null;
  formMode: 'wizard' | 'single';
  label: string | null;
  publishedProductId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ProductDraftPayload = Partial<CreateProductRequest> & {
  currentStep?: string;
  selectedCategoryIds?: string[];
  productionMethodIds?: string[];
  itemTypeId?: string | null;
  collectionId?: string | null;
  productDesign?: string | null;
  variants?: Partial<CreateVariantRequest>[];
};
